import moment from "moment";
import { db, buildOrderReferenceCacheKey } from "@/utils/dexie";
import { groupItems } from "@/utils/settlementUtils";
import { summarizeOrderSpecifiedPaymentTotal } from "@/utils/orderSpecifiedPaymentTotal";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";

/**
 * Save order and refund items into Dexie IndexedDB
 */
export async function saveOrderAndRefund(
  mallId: string,
  shopNo: string,
  orders: any[],
  refunds: any[]
): Promise<void> {
  const today = moment().format("YYYY-MM-DD");
  await Promise.all([
    ...orders
      .filter(
        (order) => moment(order.payment_date).format("YYYY-MM-DD") < today
      )
      .map((order) =>
        db.orders.put({
          date: moment(order.payment_date).format("YYYY-MM-DD"),
          mall_id: mallId,
          shop_no: shopNo,
          ...order,
        })
      ),
    ...refunds
      .filter(
        (refund) => moment(refund.refund_date).format("YYYY-MM-DD") < today
      )
      .map((refund) =>
        db.refunds.put({
          date: moment(refund.refund_date).format("YYYY-MM-DD"),
          mall_id: mallId,
          shop_no: shopNo,
          ...refund,
        })
      ),
  ]);
}

/**
 * Retrieve cached order details for reference-order linking.
 *
 * These rows are intentionally not used as proof that a payment-date range is
 * fully settled. A by-ids lookup may only contain the orders referenced by a
 * subset of refunds.
 */
export async function getOrdersByIdsFromDb(
  mallId: string,
  shopNo: string | number,
  orderIds: string[]
): Promise<any[]> {
  const uniqueOrderIds = Array.from(
    new Set(
      orderIds.map((orderId) => String(orderId || "").trim()).filter(Boolean)
    )
  );
  if (!mallId || uniqueOrderIds.length === 0) return [];

  const cacheKeys = uniqueOrderIds.map((orderId) =>
    buildOrderReferenceCacheKey(mallId, shopNo, orderId)
  );
  const rows = await db.orderRefs.bulkGet(cacheKeys);
  return rows
    .filter((row): row is any => Boolean(row))
    .map((row) => {
      const order = { ...row };
      delete order.cache_key;
      delete order.fetched_at;
      return order;
    });
}

/**
 * Persist sanitized order details for future refund reference lookups.
 *
 * Unlike the date-range settlement stores, this cache includes today's orders
 * because the same order_id should not trigger another by-ids request during a
 * later forced settlement refresh.
 */
export async function saveOrderReferences(
  mallId: string,
  shopNo: string | number,
  orders: any[]
): Promise<void> {
  if (!mallId || !Array.isArray(orders) || orders.length === 0) return;

  const uniqueOrders = new Map<string, any>();
  for (const order of orders) {
    const orderId = String(order?.order_id || "").trim();
    if (orderId) uniqueOrders.set(orderId, order);
  }

  if (uniqueOrders.size === 0) return;

  const normalizedShopNo = String(shopNo);
  const fetchedAt = Date.now();
  await db.orderRefs.bulkPut(
    Array.from(uniqueOrders.entries()).map(([orderId, order]) => ({
      ...order,
      cache_key: buildOrderReferenceCacheKey(mallId, normalizedShopNo, orderId),
      mall_id: mallId,
      shop_no: normalizedShopNo,
      order_id: orderId,
      fetched_at: fetchedAt,
    }))
  );
}

/**
 * Save aggregated daily settlement results into Dexie IndexedDB
 */
export async function saveResult(
  decoded: {
    mall_id: string;
    shop_no: string | number;
    orders?: any[];
    refunds?: any[];
  },
  missingDates: string[]
): Promise<any[]> {
  if (!decoded) return [];
  const mallId = decoded.mall_id || "";
  const shopNo = decoded.shop_no !== undefined ? String(decoded.shop_no) : "";
  const orders = decoded.orders || [];
  const refunds = decoded.refunds || [];
  const rows: Record<string, any> = {};
  const today = moment().format("YYYY-MM-DD");

  const createInitRow = () => ({
    order_count: 0,
    payment_composition_amount: 0,
    refund_count: 0,
    refund_amount: 0,
    orders: [] as any[],
    refunds: [] as any[],
  });

  for (const date of missingDates) {
    rows[date] = createInitRow();
  }

  orders.forEach((order: any) => {
    const dateOnly = moment(order.payment_date).format("YYYY-MM-DD");
    if (!rows[dateOnly]) {
      rows[dateOnly] = createInitRow();
    }
    rows[dateOnly].order_count += 1;
    rows[dateOnly].payment_composition_amount +=
      summarizeOrderSpecifiedPaymentTotal([order]).totalAmount;
    rows[dateOnly].orders.push({ ...order });
  });

  refunds.forEach((refund: any) => {
    const dateOnly = moment(refund.refund_date).format("YYYY-MM-DD");
    if (!rows[dateOnly]) {
      rows[dateOnly] = createInitRow();
    }
    rows[dateOnly].refund_count += 1;
    rows[dateOnly].refund_amount += summarizeRefundAmounts([refund]).amount;
    rows[dateOnly].refunds.push({ ...refund });
  });

  const savedResults: any[] = [];
  for (const [date, row] of Object.entries(rows)) {
    const resultItem = {
      date,
      mall_id: mallId,
      shop_no: shopNo,
      order_count: row.order_count,
      payment_composition_amount: row.payment_composition_amount,
      refund_count: row.refund_count,
      refund_amount: row.refund_amount,
      orders: row.orders,
      refunds: row.refunds,
      group_items: groupItems(row.orders.concat(row.refunds)),
      updated_at: Date.now(),
    };

    if (date < today) {
      await db.results.put(resultItem);
    }
    savedResults.push(resultItem);
  }

  return savedResults;
}

/**
 * Retrieve cached settlement results from Dexie for specific shop
 */
export async function getResultsFromDb(
  mallId: string,
  shopNo: string | number
): Promise<any[]> {
  if (!mallId) return [];
  return await db.results
    .where("[mall_id+shop_no]")
    .equals([mallId, String(shopNo)])
    .toArray();
}
